// src/features/marketAlerts/MarketAlertsPage.tsx
import { useState, type FormEvent } from 'react';
import { useGuild } from '../../lib/GuildContext';
import {
  useMarketAlerts, useCreateAlert, useUpdateAlert, useDeleteAlert,
  type AlertType, type MarketAlert, type ItemMatch,
} from './useMarketAlerts';
import { ItemSearchInput } from './ItemSearchInput';
import { ChannelPicker } from '../../components/pickers/ChannelPicker';
import { RolePicker } from '../../components/pickers/RolePicker';
import { formatRelative, formatExpiry } from '../../lib/formatTime';
import './MarketAlertsPage.css';

const SNOWFLAKE_RE = /^\d{17,20}$/;

const TYPE_LABELS: Record<AlertType, string> = {
  price_above: 'Price above',
  price_below: 'Price below',
  local_vs_player_market: 'Local below player market',
  volume_discrepancy: 'Unusual trade volume',
};

// Threshold means something different per type — gp for the two price
// types, a percentage for the other two. Labeling this correctly in
// the form matters, since "1000" means something very different for
// "price above 1000gp" versus "20% below player market".
function thresholdLabel(type: AlertType) {
  if (type === 'price_above' || type === 'price_below') return 'Threshold (gp)';
  return 'Threshold (%)';
}



function CreateAlertForm({ guildId }: { guildId: string }) {
  const [item, setItem] = useState<ItemMatch | null>(null);
  const [alertType, setAlertType] = useState<AlertType>('price_above');
  const [threshold, setThreshold] = useState('');
  const [accountForPerks, setAccountForPerks] = useState(false);
  const [channelId, setChannelId] = useState('');
  const [mentionRoleId, setMentionRoleId] = useState('');
  const [cooldownMinutes, setCooldownMinutes] = useState('60');
  const [expiresInHours, setExpiresInHours] = useState('');

  const create = useCreateAlert(guildId);
  const [justCreated, setJustCreated] = useState(false);

  const thresholdNum = Number(threshold);
  const cooldownNum = Number(cooldownMinutes);

  const itemValid = item != null;
  const thresholdValid = threshold === '' ? null : Number.isFinite(thresholdNum) && thresholdNum > 0;
  const channelValid = channelId === '' ? null : SNOWFLAKE_RE.test(channelId);
  const mentionValid = mentionRoleId === '' ? true : SNOWFLAKE_RE.test(mentionRoleId);
  const cooldownValid = Number.isFinite(cooldownNum) && cooldownNum >= 5 && cooldownNum <= 1440;

  const isValid = itemValid && thresholdValid === true && channelValid === true && mentionValid && cooldownValid;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!isValid || !item) return;

    create.mutate(
      {
        itemId: item.id,
        alertType,
        threshold: thresholdNum,
        accountForPerks: alertType === 'local_vs_player_market' ? accountForPerks : undefined,
        channelId,
        mentionRoleId: mentionRoleId || null,
        cooldownMs: cooldownNum * 60 * 1000,
        expiresAt: expiresInHours ? new Date(Date.now() + Number(expiresInHours) * 3600000).toISOString() : null,
      },
      {
        // Deliberately NOT clearing item/channelId/etc. here anymore
        // — this used to reset item to null and channelId to '',
        // both of which are treated as "invalid", not "unfilled" by
        // isValid, silently re-disabling the button immediately after
        // every successful create (same bug found and fixed in
        // ScheduledReportsPage's create form). Fields now persist so
        // a similar alert can be created without retyping anything.
        onSuccess: () => {
          setJustCreated(true);
          setTimeout(() => setJustCreated(false), 2500);
        },
      },
    );
  }

  return (
    <form onSubmit={handleSubmit} className="market-alerts-form">
      <div className="market-alerts-form__row">
        <div className="market-alerts-form__field">
          <label>Item</label>
          <ItemSearchInput selectedItem={item} onSelect={setItem} />
        </div>
        <div className="market-alerts-form__field">
          <label htmlFor="alertType">Alert type</label>
          <select id="alertType" className="input" value={alertType} onChange={(e) => setAlertType(e.target.value as AlertType)}>
            {Object.entries(TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        <div className="market-alerts-form__field">
          <label htmlFor="threshold">{thresholdLabel(alertType)}</label>
          <input id="threshold" className="input" type="number" min="0" step="any" value={threshold} onChange={(e) => setThreshold(e.target.value)} />
          {thresholdValid === false && <p className="market-alerts-form__error">Must be a positive number.</p>}
        </div>
      </div>

      {alertType === 'local_vs_player_market' && (
        <label className="market-alerts-form__checkbox">
          <input type="checkbox" checked={accountForPerks} onChange={(e) => setAccountForPerks(e.target.checked)} />
          Account for local-sale perks (Negotiation Potion, Clan Offer, Prestigious Woodworking)
        </label>
      )}

      <div className="market-alerts-form__row">
        <div className="market-alerts-form__field">
          <label htmlFor="channelId">Channel ID</label>
          <ChannelPicker guildId={guildId} value={channelId} onChange={setChannelId} placeholder="e.g. 123456789012345678" />
          {channelValid === false && <p className="market-alerts-form__error">Must be a Discord snowflake — 17 to 20 digits, no extra spaces or characters.</p>}
        </div>
        <div className="market-alerts-form__field">
          <label htmlFor="mentionRoleId">Mention role ID (optional)</label>
          <RolePicker guildId={guildId} value={mentionRoleId} onChange={setMentionRoleId} placeholder="e.g. 123456789012345678" />
          {!mentionValid && <p className="market-alerts-form__error">Must be a Discord snowflake — 17 to 20 digits.</p>}
        </div>
        <div className="market-alerts-form__field">
          <label htmlFor="cooldown">Cooldown (minutes)</label>
          <input id="cooldown" className="input" type="number" min="5" max="1440" value={cooldownMinutes} onChange={(e) => setCooldownMinutes(e.target.value)} />
        </div>
        <div className="market-alerts-form__field">
          <label htmlFor="expires">Expires in (hours, optional)</label>
          <input id="expires" className="input" type="number" min="1" value={expiresInHours} onChange={(e) => setExpiresInHours(e.target.value)} />
        </div>
      </div>

      <div>
        <button className="button-primary" type="submit" disabled={!isValid || create.isPending}>
          {create.isPending ? 'Creating…' : 'Create alert'}
        </button>
        {create.isError && <span style={{ color: 'var(--negative)', fontSize: 13, marginLeft: '1rem' }}>Couldn&apos;t create the alert. Check the fields and try again.</span>}
        {justCreated && <span style={{ color: 'var(--positive)', fontSize: 13, marginLeft: '1rem' }}>Created — change what you need and create another, or scroll down to see it.</span>}
        {!isValid && !create.isPending && (
          <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: '0.5rem' }}>
            Still needed: {[
              !itemValid && 'select an item',
              thresholdValid !== true && 'a valid threshold',
              channelValid !== true && 'a valid channel ID',
              !mentionValid && 'a valid mention role ID (or leave it blank)',
              !cooldownValid && 'a cooldown between 5 and 1440 minutes',
            ].filter(Boolean).join(', ')}.
          </p>
        )}
      </div>
    </form>
  );
}

function AlertRow({ guildId, alert, index }: { guildId: string; alert: MarketAlert; index: number }) {
  const update = useUpdateAlert(guildId);
  const del = useDeleteAlert(guildId);

  const thresholdDisplay = alert.alert_type === 'price_above' || alert.alert_type === 'price_below'
    ? `${Math.round(Number(alert.threshold)).toLocaleString()} gp`
    : `${Number(alert.threshold)}%`;

  return (
    <tr className={index % 2 === 1 ? 'zebra-row--even' : undefined}>
      <td className="market-alerts-table__item">{alert.item_name}</td>
      <td>{TYPE_LABELS[alert.alert_type]}</td>
      <td className="market-alerts-table__mono">{thresholdDisplay}</td>
      <td className="market-alerts-table__mono">&lt;#{alert.channel_id}&gt;</td>
      <td className="market-alerts-table__mono">{alert.mention_role_id ? `<@&${alert.mention_role_id}>` : 'None'}</td>
      <td className="market-alerts-table__mono">{formatRelative(alert.last_triggered_at)}</td>
      <td className="market-alerts-table__mono">{formatExpiry(alert.expires_at)}</td>
      <td>
        <button
          className={`toggle${alert.enabled ? ' toggle--on' : ''}`}
          role="switch"
          aria-checked={alert.enabled}
          aria-label={alert.enabled ? 'Pause alert' : 'Resume alert'}
          onClick={() => update.mutate({ id: alert.id, enabled: !alert.enabled })}
          disabled={update.isPending}
        >
          <span className="toggle__thumb" />
        </button>
      </td>
      <td>
        <button className="button-danger" onClick={() => del.mutate(alert.id)} disabled={del.isPending}>
          {del.isPending ? 'Removing…' : 'Remove'}
        </button>
      </td>
    </tr>
  );
}

export function MarketAlertsPage() {
  const { selectedGuildId } = useGuild();
  const { data: alerts, isLoading, error } = useMarketAlerts(selectedGuildId);

  return (
    <>
      <div className="page-header">
        <p className="page-header__eyebrow">Price and volume conditions</p>
        <h1 className="page-header__title">Market Alerts</h1>
      </div>

      <article className="card" style={{ marginBottom: '1.25rem' }}>
        <header className="card__header">
          <div>
            <p className="card__eyebrow">New alert</p>
            <h2 className="card__title">Create a condition</h2>
          </div>
        </header>
        <div className="card__content">
          {selectedGuildId && alerts && alerts.length >= 3 && (
            <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
              Maximum of 3 market alerts per server reached. Delete one below to create another.
            </p>
          )}
          {selectedGuildId && (!alerts || alerts.length < 3) && <CreateAlertForm guildId={selectedGuildId} />}
        </div>
      </article>

      <article className="card">
        <header className="card__header">
          <div>
            <p className="card__eyebrow">{alerts?.length ?? 0} alerts</p>
            <h2 className="card__title">Active conditions</h2>
          </div>
        </header>
        <div className="card__content">
          {isLoading && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</p>}
          {error && <p style={{ color: 'var(--negative)', fontSize: 13 }}>Couldn&apos;t load alerts. Try refreshing.</p>}
          {alerts?.length === 0 && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No market alerts set up yet.</p>}
          {alerts && alerts.length > 0 && selectedGuildId && (
            <table className="market-alerts-table">
              <thead>
                <tr>
                  <th>Item</th><th>Type</th><th>Threshold</th><th>Channel</th><th>Mention role</th><th>Last triggered</th><th>Expires</th><th>Enabled</th><th></th>
                </tr>
              </thead>
              <tbody>
                {alerts.map((alert, index) => (
                  <AlertRow key={alert.id} guildId={selectedGuildId} alert={alert} index={index} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </article>
    </>
  );
}
